import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Container, makeStyles, useTheme } from '@material-ui/core';
import {
  UNABLE_TO_LOAD_AVAILABLE_DATES_AND_TIMES_FOR_VENUES,
  SIGNATURE_COLLECTION_TIMESLOT_TEXT,
  RETURN_HOME_LINK_TEXT,
  INLINE_AVAILABILITY_ERROR_MESSAGE,
} from '../../../assets/copy';
import Typography from '../../overrides/Typography';
import { TimeButton, TimeButtonGroup } from '../../core/TimesButton';
import { ReactComponent as BenefitsIcon } from '../../../assets/icons/grv-benefits.svg';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';
import CalendarSelector from '../../core/CalendarSelector';
import NoCalendarAvailabilityInfo from '../../../pages/venueAvailability/NoCalendarAvailabilityInfo';
import { focusVisible } from '../../../utils/ada/contrastStyles';
import DirectionalButton from '../../buttons/DirectionalButton';
import { useViewport } from '../../../utils/useViewport';
import { WIDTH_BREAKPOINT } from '../../../utils/constants/Breakpoints';
import { useHistory } from 'react-router';
import WarningThick from '../../../assets/icons/warning-thick.svg';
import InfoBlockMessage from '../../core/InfoBlock/InfoBlockMessage';
import { trackErrorPageView } from '../../../utils/useOneTag';
import ErrorPageView from '../../../utils/ErrorPageView';
import { useCopy } from '../../../utils/useCopy';
import { actionGetDateRangeAvailabilityReset } from '../../../store/Availability/AvailabilityAction';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    alignItems: 'center',
    paddingBottom: theme.spacing(1),
    overflowX: 'hidden',
  },
  exclusiveExplainerContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    margin: theme.spacing(2.5, 0, 3),
    '& > *': {
      fontSize: 12,
    },
  },
  timeButtonContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    overflowX: 'clip',
  },
  dateHeading: {
    textAlign: 'left',
    width: '100%',
    margin: theme.spacing(1.5, 0),
  },
  benefitsIcon: {
    width: 28,
    height: 28,
    flexShrink: 0,
    '& path': {
      fill: `${theme.palette.spoon.yellow[50]} !important`,
    },
  },
  arrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: '50%',
    ...focusVisible(), 
    '&:hover': {
      background: theme.palette.background.grayLight, 
      opacity: 0.93,
    },
  },
  timeslotRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
  },
  inlineError: {
    margin: theme.spacing(1, 0, 2),
    [theme.breakpoints.up('xl')]: {
      marginRight: 0,
    },
  },
}));

export const ChooseDate = ({ venue, onTimeButtonClick, onClose }) => {
  const {
    list: availabilities,
    loadingAvailability,
    submittedGuests,
    calendarVisibleMonth,
    dateRangeAvailabilityFailure,
  } = useSelector((state) => state.availability);
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme(); 
  
  const { width } = useViewport();
  const isMobile = width <= WIDTH_BREAKPOINT;
  const timeslots = availabilities?.[0]?.timeslots || [];

  // Desktop carousel logic
  const visibleCount = 5; // Slots will show 4 full + 1 partial 
  const slideBy = 4; // Number of slots to move when clicking arrows
  const [startIdx, setStartIdx] = useState(0);
  const maxStartIdx = Math.max(0, timeslots.length - slideBy); 
  const endIdx = Math.min(startIdx + visibleCount, timeslots.length);
  
  const handlePrev = () => setStartIdx(Math.max(0, startIdx - slideBy));
  const handleNext = () => setStartIdx(Math.min(maxStartIdx, startIdx + slideBy));
  
  const { getCopy } = useCopy();
  const dispatch = useDispatch();

  const closeDialog = () => {
    onClose();
    history.push('/');
    dispatch(actionGetDateRangeAvailabilityReset());
  };

  useEffect(() => {
    if (dateRangeAvailabilityFailure) {
      trackErrorPageView(
        new ErrorPageView({
          errorTitle: getCopy(
            'AVAILABILITY_ERROR_IN_BOOK_A_TABLE_BOTTOM_SHEET_TITLE',
            { venueName: venue?.name, venueId: venue?.id }
          ),
          errorMessage: INLINE_AVAILABILITY_ERROR_MESSAGE,
          value: 'AVAILABILITY_ERROR',
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeAvailabilityFailure, venue?.id, venue?.name]);

  if (dateRangeAvailabilityFailure) {
    return (
      <Container className={classes.container}>
        <InfoBlockMessage
          icon={WarningThick}
          text={UNABLE_TO_LOAD_AVAILABLE_DATES_AND_TIMES_FOR_VENUES}
          screenReaderText={UNABLE_TO_LOAD_AVAILABLE_DATES_AND_TIMES_FOR_VENUES}
          linkText={RETURN_HOME_LINK_TEXT}
          onKeyPressHandler={(e) => {
            if (e.key === 'Enter') {
              closeDialog();
            }
          }}
          onClickHandler={() => {
            closeDialog();
          }}
          classes={{ root: classes.inlineError }}
          role="alert"
          altIconText="Warning icon"
        />
      </Container>
    );
  }

  return (
    <>
      <Container className={classes.container}>
        <CalendarSelector
          venue={venue}
          guests={submittedGuests}
          loadingAvailability={loadingAvailability}
          showCalendarShadow={false}
        />
        {timeslots.length > 0 && (
          <Typography
            variant="medium1Semibold"
            className={classes.dateHeading}
            component="h3">
            {DateTime.fromISO(availabilities[0].date).toLocaleString({
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        )}
      </Container>
      <div 
        aria-label={SIGNATURE_COLLECTION_TIMESLOT_TEXT} 
        className={classes.timeslotRow}
        style={{
          paddingLeft: timeslots.length >= visibleCount ? theme.spacing(1) : 0,
          paddingRight: timeslots.length >= visibleCount ? theme.spacing(1) : 0,
        }}
      >
        {isMobile ? (
          <TimeButtonGroup className={classes.timeButtonContainer} bufferWidth={'16px'}>
            {availabilities?.[0].timeslots?.map((timeslot, i) => (
              <TimeButton
                key={`timeslot_${i}`}
                onClick={() =>
                  onTimeButtonClick(
                    availabilities[0].date,
                    timeslot,
                    parseInt(submittedGuests)
                  )
                }
                venueParent={{ isInsider: timeslot.isInsider }}
                timeslot={timeslot}
                text={timeslot.timeSlot}
              />
            ))}
          </TimeButtonGroup>
        ) : (
          <>
            {timeslots.length >= visibleCount && (
              startIdx > 0 ? (
                <DirectionalButton
                  isRippleDisabled
                  buttonClassName={classes.arrow}
                  onClickHandler={handlePrev}
                  direction={'left'}
                  aria-label="Previous timeslots"
                />
              ) : (
                <div className={classes.arrow} style={{ visibility: 'hidden' }} />
              )
          )}
            <TimeButtonGroup
              className={classes.timeButtonContainer}
              bufferWidth={timeslots.length < visibleCount ? '16px' : undefined}
            >
              {availabilities?.[0]?.timeslots?.slice(startIdx, endIdx).map((timeslot, i) => (
                <TimeButton
                  key={`timeslot_${startIdx + i}`}
                  onClick={() =>
                    onTimeButtonClick(
                      availabilities[0].date,
                      timeslot,
                      parseInt(submittedGuests)
                    )
                  }
                  venueParent={{ isInsider: timeslot.isInsider }}
                  timeslot={timeslot}
                  text={timeslot.timeSlot}
                />
              ))}
            </TimeButtonGroup>
            {timeslots.length >= visibleCount && (
              startIdx < maxStartIdx ? (
                <DirectionalButton
                  isRippleDisabled
                  buttonClassName={classes.arrow}
                  onClickHandler={handleNext}
                  direction={'right'}
                  aria-label="Next timeslots"
                />
              ) : (
                <div className={classes.arrow} style={{ visibility: 'hidden' }} />
              )
            )}
          </>
        )}
      </div>
      {availabilities?.[0].timeslots?.length === 0 &&(
        <Container>
          <NoCalendarAvailabilityInfo
            date={calendarVisibleMonth}
            useDivider={false}
          />
        </Container>
      )}
      <Container className={classes.exclusiveExplainerContainer}>
        {venue?.isSignatureCollection && (
          <>
            <BenefitsIcon
              className={classes.benefitsIcon}
              role="img"
              aria-labelledby="exclusiveCardholderTableIcon">
              <p id="exclusiveCardholderTableIcon"></p>
            </BenefitsIcon>
            <Typography
              variant="small1Normal"
              id="exclusiveCardholderTableIcon">
              {SIGNATURE_COLLECTION_TIMESLOT_TEXT}
            </Typography>
          </>
        )}
      </Container>
    </>
  );
};

ChooseDate.propTypes = {
  venue: PropTypes.shape({
    sevenRoomsVenueId: PropTypes.string.isRequired,
    experienceId: PropTypes.string,
  }).isRequired,
  onTimeButtonClick: PropTypes.func.isRequired,
};